import { ExternalLinkIcon } from "@chakra-ui/icons";
import {
  Text,
  HStack,
  Grid,
  Box,
  VStack,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Link,
  Divider,
} from "@chakra-ui/react";
import { sumWithDecimal } from "@syla/shared/decimal";
import { TaxYearResultBase } from "@syla/shared/types/models/TaxYearResultBase";
import React, { Fragment } from "react";
import { CARD_FULL_WIDTH } from "../../components/molecules/ActionCard";
import { NumberStyler } from "../../helper/NumberStyler";
import { useGlobalVariablesStore } from "../../store/globalVariablesStore";
import { CardHeading } from "./CardHeading";
import { Plan } from "./Plan";
import { PlanAccount } from "./PlanAccount";
import { StdPlanText } from "./StdPlanText";
import { UnlockPlanOrAlternative } from "./UnlockPlanOrAlternative";
import { UpsellCardBase } from "./UpsellCardBase";
import { useUpsellAnalytics } from "./useUpsellAnalytics";
import { formatAsCurrency, formatFinancialYear } from "./utils";

export const TaxSavingCard = ({
  yearlySavings,
  recommendedPlan,
  cheaperPlan,
  currentPlan,
  account,
}: {
  yearlySavings: TaxYearResultBase<string>[];
  recommendedPlan: Plan;
  cheaperPlan: Plan;
  currentPlan: Plan | undefined;
  account: PlanAccount;
}) => {
  useUpsellAnalytics("taxSaving", recommendedPlan.name);

  const selectedFinancialYear = useGlobalVariablesStore(
    (state) => state.selectedFinancialYear,
  );

  const totalGain = sumWithDecimal(yearlySavings, "gain");

  return (
    <UpsellCardBase
      heading={
        <VStack alignItems="flex-start">
          <Text fontSize="xl" fontWeight="bold">
            Reduce your taxable income by
          </Text>
          <CardHeading color="green.600">
            {formatAsCurrency(totalGain)}
          </CardHeading>
        </VStack>
      }
      content={
        <>
          {/* View Calculation expander */}
          <Accordion w="full" allowToggle={true}>
            <AccordionItem style={{ borderStyle: "none" }}>
              <AccordionPanel px={0}>
                <VStack spacing={6} w="full" alignItems="flex-start">
                  {/* Savings by financial year */}
                  <VStack alignItems="flex-start" w="full">
                    <Text fontSize="medium" fontWeight="bold">
                      Savings by financial year
                    </Text>
                    <Grid templateColumns={"1fr 1fr"} w="full" gap={1}>
                      {yearlySavings
                        .filter(({ gain }) => !gain.isZero())
                        .map(({ financialYear, gain }) => (
                          <Fragment key={financialYear}>
                            <Box>
                              <Text>{formatFinancialYear(financialYear)}</Text>
                            </Box>
                            <NumberStyler
                              num={gain}
                              unit="currency"
                              negativeColored
                              textAlign="right"
                            />
                          </Fragment>
                        ))}
                      <Divider />
                      <Divider />
                      <Box>
                        <Text fontWeight="bold">Total Saving</Text>
                      </Box>
                      <NumberStyler
                        num={totalGain}
                        unit="currency"
                        negativeColored
                        textAlign="right"
                        fontWeight="bold"
                      />
                    </Grid>
                  </VStack>

                  {/* Explanation */}
                  <VStack
                    spacing={2}
                    alignItems="flex-start"
                    w="full"
                    color={"gray.600"}
                  >
                    <Text>
                      The saving is the total amount you can{" "}
                      <b>reduce your capital gains</b> or{" "}
                      <b>increase your capital losses</b> across all financial
                      years from 1 July{" "}
                      {selectedFinancialYear?.startDate.getFullYear()} to the
                      present.
                    </Text>
                    <Text>
                      Reducing your capital gains lowers your taxable income.
                      The actual tax benefit depends on your overall tax
                      situation, including your marginal tax rate.
                    </Text>
                    <Text>
                      If you later edit your transactions or apply a different
                      parcel-matching method, this saving amount will change.
                    </Text>
                  </VStack>
                </VStack>
              </AccordionPanel>

              {/* Expander button container */}
              <VStack
                position="relative"
                alignItems="center"
                justifyContent="center"
                w="full"
                mt="1rem"
                mb="2rem"
                spacing={0}
              >
                <Divider h="1px" borderColor="gray.400" w={CARD_FULL_WIDTH} />
                <VStack position="absolute" spacing={0}>
                  <Box
                    bgColor="white"
                    borderWidth="1px"
                    borderStyle="solid"
                    borderColor="gray.700"
                    borderRadius="md"
                    shadow="sm"
                    position="relative"
                  >
                    <AccordionButton
                      alignSelf="center"
                      data-event="upsell-info-toggle"
                    >
                      <AccordionIcon />
                    </AccordionButton>
                  </Box>
                  <Text
                    position="absolute"
                    bottom="-1.25rem"
                    w="full"
                    align="center"
                    color="gray.600"
                  >
                    more
                  </Text>
                </VStack>
              </VStack>
            </AccordionItem>
          </Accordion>

          {/* Std plan description */}
          <StdPlanText
            recommendedPlan={recommendedPlan}
            currentPlan={currentPlan}
            account={account}
            subtext={`Reduce your taxable income by ${formatAsCurrency(
              totalGain,
            )}`}
          />
          {/* Learn More */}
          <HStack whiteSpace="nowrap" color="primary.700">
            <Link
              href="https://help.syla.com.au/en/articles/8458954-tax-saving-calculation"
              isExternal={true}
            >
              <Text>learn more</Text>
            </Link>
            <ExternalLinkIcon />
          </HStack>
        </>
      }
      actions={
        <UnlockPlanOrAlternative
          recommendedPlan={recommendedPlan}
          cheaperPlan={cheaperPlan}
          currentPlan={currentPlan}
          account={account}
        />
      }
    />
  );
};
